import React, { useState } from 'react';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Card } from 'src/modules/shared/components/atoms/cards';
import { type Task, type TaskUpdateBodyParams } from 'src/api/generated';
import { InlineDateInput } from 'src/modules/shared/components/molecules/inputs/inline/inline-date-input';
import {
  PiX,
  PiChatCircleText,
  PiCalendar,
  PiCalendarSlash,
  PiArrowSquareOut,
} from 'react-icons/pi';
import Loading from 'src/modules/shared/components/atoms/loading';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import { Text } from 'src/modules/shared/components/atoms/text';
import { ConfirmModal } from 'src/modules/shared/components/organisms/modal';

import { TaskStatusIcon } from './task-status-icon';
import { TaskObjectiveInput } from './task-objective-input';
import { TaskAssigneeDropdown } from './task-assignee-dropdown';
import { useTaskOperations } from '../../../hooks/tasks/use-task-operations';
import { getDisplayDate, isTaskOverdue } from '../../../utils/tasks/date.util';
import { formatDateForAPI } from '../../../utils/formatting/date.util';
/**
 * Props for the TaskItem component
 */
interface TaskItemProps {
  /** The task object to display */
  task: Task;
  /** Whether to show a link to the conversation */
  showConversationLink?: boolean;
}

/**
 * Task item component
 */
export const TaskItem: React.FC<TaskItemProps> = ({
  task,
  showConversationLink = false,
}) => {
  const [isAddingDate, setIsAddingDate] = useState(false);
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);

  const {
    handleToggleComplete,
    handleTaskUpdate,
    handleCancelTask,
    getTooltipContent,
    isTaskFinished,
    updatingTask,
  } = useTaskOperations();

  const handleAssigneeChange = async (
    taskId: string,
    assignedTo: NonNullable<TaskUpdateBodyParams['assignedTo']>
  ) => {
    await handleTaskUpdate(taskId, 'assignedTo', assignedTo);
  };

  const handleGoToConversation = (conversationId: number) => {
    const searchParams = new URLSearchParams({
      conversationId: conversationId.toString(),
    });
    const url = `/communications?${searchParams.toString()}`;
    window.open(url, '_blank');
  };

  const handleToggleDueDate = async () => {
    // Start adding a date (show input for editing)
    if (!task.dueDate && !isAddingDate) {
      setIsAddingDate(true);
      return;
    }

    // Remove due date or cancel adding
    if (task.dueDate) {
      await handleTaskUpdate(task.id, 'dueDate', null);
    }
    setIsAddingDate(false);
  };

  const handleDateSave = async (value: string) => {
    await handleTaskUpdate(task.id, 'dueDate', value);
    setIsAddingDate(false);
  };

  const finished = isTaskFinished(task);
  const displayDate = getDisplayDate(task) ?? formatDateForAPI(new Date());
  const overdue = isTaskOverdue(task);
  const showDateInput = Boolean(task.dueDate) || isAddingDate;

  return (
    <>
      <Card
        variant="neutral"
        appearance="outline"
        size="sm"
        shadow="none"
        className="px-2 py-0.5 relative"
      >
        {updatingTask && (
          <div className="absolute inset-0 bg-white/80 rounded-lg flex items-center justify-center z-10">
            <Loading size="sm" variant="neutral" />
          </div>
        )}

        <div className="flex py-1 items-center gap-2 flex-wrap justify-end">
          {/* Status Toggle Button with shared icon */}
          <div className="mr-auto">
            <Tooltip
              content={getTooltipContent(task)}
              placement="top"
              size="xs"
            >
              <Button
                onClick={() => handleToggleComplete(task)}
                className="flex-shrink-0 rounded-full"
                variant="primary"
                appearance="ghost"
                isIconOnly
                size="sm"
                disabled={updatingTask}
              >
                <TaskStatusIcon task={task} />
              </Button>
            </Tooltip>
          </div>

          {/* Objective Input */}
          <div className="flex-1 min-w-[180px] pl-2">
            <TaskObjectiveInput
              value={task.objective}
              onSave={async (value) =>
                await handleTaskUpdate(task.id, 'objective', value)
              }
              disabled={updatingTask}
              isFinished={finished}
              size="sm"
            />
          </div>

          {/* Date Input - Toggle button and date input combined */}
          <div className="flex flex-shrink-0 items-center whitespace-nowrap">
            <Tooltip
              // TODO: check when implementing i18n
              content={
                <Text size="small">
                  {showDateInput
                    ? 'Quitar fecha límite'
                    : 'Agregar fecha límite'}
                </Text>
              }
              placement="top"
              size="xs"
            >
              <Button
                onClick={handleToggleDueDate}
                variant={showDateInput ? 'neutral' : 'primary'}
                appearance="soft"
                size="sm"
                isIconOnly
                disabled={updatingTask || finished}
                className="flex-shrink-0"
              >
                {showDateInput ? (
                  <PiCalendarSlash className="h-4 w-4" />
                ) : (
                  <PiCalendar className="h-4 w-4" />
                )}
              </Button>
            </Tooltip>

            {showDateInput && (
              <InlineDateInput
                label=""
                value={displayDate}
                onSave={handleDateSave}
                type="datetime-local"
                disableCopy
                buttonLabelClassName={`${finished ? 'text-primary-400' : ''}`}
                disabled={finished || updatingTask}
                variant={overdue ? 'danger' : 'neutral'}
                size="sm"
              />
            )}
          </div>

          <div className="flex flex-shrink-0 items-center whitespace-nowrap">
            <TaskAssigneeDropdown
              task={task}
              onAssigneeChange={handleAssigneeChange}
              disabled={updatingTask}
              isFinished={finished}
              size="2xs"
            />
          </div>

          {/* Action buttons area */}
          <div className="flex flex-shrink-0 items-center whitespace-nowrap gap-2">
            {/* Conversation link button */}
            {showConversationLink && task.context.conversationId && (
              <Tooltip
                content={
                  <div className="flex items-center gap-1">
                    {/* TODO: check when implementing i18n */}
                    <Text size="small">Ir a conversación</Text>
                    <PiArrowSquareOut className="h-4 w-4" />
                  </div>
                }
                placement="top"
                size="xs"
              >
                <Button
                  onClick={() => {
                    if (task.context.conversationId) {
                      handleGoToConversation(task.context.conversationId);
                    }
                  }}
                  className="flex-shrink-0"
                  appearance="ghost"
                  variant="neutral"
                  isIconOnly
                  size="sm"
                  disabled={updatingTask}
                >
                  <PiChatCircleText className="h-5 w-5" />
                </Button>
              </Tooltip>
            )}

            {/* Cancel button (only for non-finished tasks) */}
            {!finished && (
              <Tooltip
                // TODO: check when implementing i18n
                content={<Text size="small">Cancelar tarea</Text>}
                placement="top"
                size="xs"
              >
                <Button
                  onClick={() => setIsCancelConfirmOpen(true)}
                  className="flex-shrink-0"
                  appearance="ghost"
                  variant="danger"
                  isIconOnly
                  size="xs"
                  disabled={updatingTask}
                >
                  <PiX className="h-5 w-5" />
                </Button>
              </Tooltip>
            )}
          </div>
        </div>
      </Card>

      <ConfirmModal
        isOpen={isCancelConfirmOpen}
        onClose={() => setIsCancelConfirmOpen(false)}
        // TODO: check when implementing i18n
        title="Cancelar tarea"
        variant="danger"
        confirmText="Cancelar tarea"
        cancelText="Volver"
        loading={updatingTask}
        onConfirm={async () => {
          await handleCancelTask(task.id);
          setIsCancelConfirmOpen(false);
        }}
      >
        {/* TODO: check when implementing i18n */}
        <Text size="small">
          ¿Seguro que quieres cancelar esta tarea? Puedes revertirlo marcándola
          como pendiente.
        </Text>
      </ConfirmModal>
    </>
  );
};
